import React, { useState, useEffect, useRef } from 'react';
import { http } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import SignatureCanvas from 'react-signature-canvas';
import { Plus, Search, X, Save, FileText, Edit, Trash2, Eye, ChevronRight, UserPlus, Lock } from 'lucide-react';
import { toast } from 'sonner';

const TIPOS = [
  { value: 'factura', label: 'Factura' },
  { value: 'recibo',  label: 'Recibo' },
];
const ESTADOS = [
  { value: 'pendiente_entrega', label: 'Pendiente entrega', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'entregado',         label: 'Entregado',         color: 'bg-green-100 text-green-800' },
];

function Badge({ estado }) {
  const e = ESTADOS.find(x => x.value === estado) || ESTADOS[0];
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${e.color}`}>{e.label}</span>;
}

export default function EntregasDocumentosList() {
  const { user } = useAuth();
  const [entregas, setEntregas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [asignandoDoc, setAsignandoDoc] = useState(null);
  const esAdmin = user?.rol === 'admin';

  useEffect(() => { cargar(); }, []);

  async function cargar() {
    setLoading(true);
    try {
      const [eRes, cRes] = await Promise.all([http.get('/entregas-documentos'), http.get('/clientes')]);
      setEntregas(eRes.data || []);
      setClientes(cRes.data || []);
      if (esAdmin) { const uRes = await http.get('/usuarios'); setTecnicos((uRes.data||[]).filter(u=>u.rol==='tecnico')); }
    } catch { toast.error('Error cargando entregas'); }
    finally { setLoading(false); }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar esta entrega?')) return;
    try { await http.delete(`/entregas-documentos/${id}`); toast.success('Eliminada'); cargar(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  }

  async function guardarAsignacion(id, data) {
    try {
      await http.patch(`/entregas-documentos/${id}/asignar`, data);
      toast.success('Asignación guardada');
      setAsignandoDoc(null);
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al asignar'); }
  }

  function esLocked(e) { return e.estado === 'entregado' && !!e.firma_cliente; }
  function fmtDate(d) { return d ? new Date(d).toLocaleDateString('es-PY') : '—'; }

  const filtradas = entregas.filter(e => {
    const q = busqueda.toLowerCase();
    const ok = !q || [e.numero_documento, e.nombre_cliente, e.tipo_documento].some(v=>(v||'').toLowerCase().includes(q));
    return ok && (!filtroEstado || e.estado === filtroEstado);
  });

  return (
    <div className="p-3 sm:p-4 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" /> Entrega de Documentos
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">Registro de entrega de recibos y facturas</p>
        </div>
        <button onClick={() => { setEditando(null); setShowForm(true); }}
          className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white px-4 py-3 sm:py-2 rounded-xl text-sm font-medium w-full sm:w-auto">
          <Plus className="w-4 h-4" /> Nueva entrega
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Buscar por número, cliente..." value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        </div>
        <select className="border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
        </select>
      </div>

      {loading ? <div className="text-center py-12 text-gray-500">Cargando...</div>
      : filtradas.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No hay entregas registradas</p>
        </div>
      ) : (
        <>
          {/* Movil: cards */}
          <div className="sm:hidden space-y-2">
            {filtradas.map(e => (
              <div key={e.id||e._id} className="bg-white border rounded-2xl p-4 shadow-sm active:bg-gray-50"
                onClick={() => setDetalle(e)}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div>
                    <p className="font-semibold text-gray-900 text-sm capitalize">{e.tipo_documento} {e.numero_documento}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {e.tecnico_id
                        ? <span>🔧 {e.nombre_tecnico || e.tecnico_id}</span>
                        : <span className="text-orange-500 font-medium">Sin asignar</span>}
                    </p>
                    {e.cliente_id && <p className="text-xs text-gray-500">👤 {e.nombre_cliente || e.cliente_id}</p>}
                  </div>
                  <Badge estado={e.estado} />
                </div>
                <p className="text-xs text-gray-400 mb-3">Cargado: {fmtDate(e.fecha_carga || e.created_at)}</p>
                <div className="flex items-center justify-end gap-2" onClick={ev=>ev.stopPropagation()}>
                  {!esLocked(e) && (
                    <button onClick={() => setAsignandoDoc(e)}
                      className="flex items-center gap-1 text-xs bg-orange-50 text-orange-700 px-3 py-2 rounded-xl font-medium active:bg-orange-100">
                      <UserPlus className="w-3.5 h-3.5" /> Asignar
                    </button>
                  )}
                  {esLocked(e) && <Lock className="w-3.5 h-3.5 text-gray-300" />}
                  <button onClick={() => { setEditando(e); setShowForm(true); }}
                    className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-3 py-2 rounded-xl font-medium active:bg-blue-100">
                    <Edit className="w-3.5 h-3.5" /> Editar
                  </button>
                  {esAdmin && <button onClick={() => eliminar(e.id||e._id)}
                    className="flex items-center gap-1 text-xs bg-red-50 text-red-600 px-3 py-2 rounded-xl font-medium active:bg-red-100">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>}
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </div>
              </div>
            ))}
          </div>
          {/* Desktop: tabla */}
          <div className="hidden sm:block bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Tipo','N° Documento','Técnico / Cliente','Estado','Cargado','Acciones'].map(h=>(
                      <th key={h} className={`px-4 py-3 font-medium text-gray-500 ${h==='Acciones'?'text-right':'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtradas.map(e => (
                    <tr key={e.id||e._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 capitalize">{e.tipo_documento}</span></td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-700">{e.numero_documento}</td>
                      <td className="px-4 py-3">
                        {e.tecnico_id
                          ? <><p className="text-sm text-gray-700">{e.nombre_tecnico||e.tecnico_id}</p>{e.cliente_id&&<p className="text-xs text-gray-400">{e.nombre_cliente||e.cliente_id}</p>}</>
                          : <span className="text-xs font-medium text-orange-500">Sin asignar</span>}
                      </td>
                      <td className="px-4 py-3"><Badge estado={e.estado} /></td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(e.fecha_carga||e.created_at)}</td>
                      <td className="px-4 py-3"><div className="flex justify-end gap-1">
                        {!esLocked(e) && <button title="Asignar persona" onClick={()=>setAsignandoDoc(e)} className="p-1.5 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-md"><UserPlus className="w-4 h-4"/></button>}
                        <button onClick={()=>setDetalle(e)} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md"><Eye className="w-4 h-4"/></button>
                        <button onClick={()=>{setEditando(e);setShowForm(true);}} className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md"><Edit className="w-4 h-4"/></button>
                        {esAdmin&&<button onClick={()=>eliminar(e.id||e._id)} className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md"><Trash2 className="w-4 h-4"/></button>}
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showForm && <EntregaModal entrega={editando} clientes={clientes} tecnicos={tecnicos} user={user}
        onClose={()=>{setShowForm(false);setEditando(null);}} onSaved={()=>{setShowForm(false);setEditando(null);cargar();}} />}
      {detalle && <DetalleModal entrega={detalle} onClose={()=>setDetalle(null)}
        onEdit={()=>{setDetalle(null);setEditando(detalle);setShowForm(true);}}
        onAsignar={()=>{setAsignandoDoc(detalle);setDetalle(null);}} esLocked={esLocked(detalle)} />}
      {asignandoDoc && <AsignarModal entrega={asignandoDoc} clientes={clientes} tecnicos={tecnicos}
        onClose={()=>setAsignandoDoc(null)} onSave={data=>guardarAsignacion(asignandoDoc.id||asignandoDoc._id, data)} />}
    </div>
  );
}

function EntregaModal({ entrega, clientes, tecnicos, user, onClose, onSaved }) {
  const esAdmin = user?.rol === 'admin';
  const esEdicion = !!entrega;
  const sigRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const uid = user?.id || user?._id || '';

  const [form, setForm] = useState({
    tipo_documento:       entrega?.tipo_documento       || 'factura',
    numero_documento:     entrega?.numero_documento     || '',
    cliente_id:           entrega?.cliente_id           || '',
    nombre_cliente:       entrega?.nombre_cliente       || '',
    tecnico_id:           entrega?.tecnico_id           || uid,
    nombre_tecnico:       entrega?.nombre_tecnico       || (user?.nombre||user?.username||''),
    estado:               entrega?.estado               || 'pendiente_entrega',
    fecha_entrega_tecnico:entrega?.fecha_entrega_tecnico? new Date(entrega.fecha_entrega_tecnico).toISOString().slice(0,10) : new Date().toISOString().slice(0,10),
    fecha_entrega_cliente:entrega?.fecha_entrega_cliente? new Date(entrega.fecha_entrega_cliente).toISOString().slice(0,10) : '',
    observaciones:        entrega?.observaciones        || '',
    orden_id:             entrega?.orden_id             || '',
    aclaracion_firma_cliente: entrega?.aclaracion_firma_cliente || '',
  });

  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  async function guardar() {
    if (!form.numero_documento) return toast.error('Número de documento obligatorio');
    setSaving(true);
    try {
      const firma = sigRef.current && !sigRef.current.isEmpty() ? sigRef.current.toDataURL() : (entrega?.firma_cliente || null);
      const payload = {
        ...form,
        firma_cliente: firma,
        fecha_entrega_tecnico: new Date(form.fecha_entrega_tecnico).toISOString(),
        fecha_entrega_cliente: form.fecha_entrega_cliente ? new Date(form.fecha_entrega_cliente).toISOString() : null,
      };
      if (esEdicion) { await http.put(`/entregas-documentos/${entrega.id||entrega._id}`, payload); toast.success('Actualizada'); }
      else           { await http.post('/entregas-documentos', payload); toast.success('Creada'); }
      onSaved();
    } catch(e) { toast.error(e.response?.data?.detail||'Error al guardar'); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-xl flex flex-col max-h-[92vh] sm:max-h-[85vh]">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b flex-shrink-0 relative">
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full sm:hidden" />
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 mt-1 sm:mt-0">{esEdicion?'Editar entrega':'Nueva entrega de documento'}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Tipo *</label>
              <div className="flex gap-2">
                {TIPOS.map(t => (
                  <button key={t.value} type="button" onClick={() => set('tipo_documento', t.value)}
                    className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-colors ${form.tipo_documento===t.value?'bg-blue-600 text-white border-blue-600':'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <FField label="N° Documento *" value={form.numero_documento} onChange={v=>set('numero_documento',v)} placeholder="Ej: 001-001-000001" color="blue" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FSelect label="Cliente (opcional)" value={form.cliente_id} onChange={v=>{const c=clientes.find(c=>(c.id||c._id)===v);set('cliente_id',v);set('nombre_cliente',c?.nombre||'');}} color="blue">
              <option value="">Sin asignar</option>
              {clientes.map(c=><option key={c.id||c._id} value={c.id||c._id}>{c.nombre}</option>)}
            </FSelect>
            {esAdmin
              ? <FSelect label="Técnico (opcional)" value={form.tecnico_id} onChange={v=>{const t=tecnicos.find(t=>(t.id||t._id)===v);set('tecnico_id',v);set('nombre_tecnico',t?.nombre||t?.username||'');}} color="blue">
                  <option value="">Sin asignar</option>
                  {tecnicos.map(t=><option key={t.id||t._id} value={t.id||t._id}>{t.nombre||t.username}</option>)}
                </FSelect>
              : <FField label="Técnico" value={form.nombre_tecnico} readOnly color="blue" />
            }
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FSelect label="Estado" value={form.estado} onChange={v=>set('estado',v)} color="blue">
              {ESTADOS.map(e=><option key={e.value} value={e.value}>{e.label}</option>)}
            </FSelect>
            <FField label="Fecha entrega a técnico" type="date" value={form.fecha_entrega_tecnico} onChange={v=>set('fecha_entrega_tecnico',v)} color="blue" />
          </div>

          <FField label="Fecha entrega al cliente" type="date" value={form.fecha_entrega_cliente} onChange={v=>set('fecha_entrega_cliente',v)} color="blue" />
          <FField label="Vínculo Orden (opcional)" value={form.orden_id} onChange={v=>set('orden_id',v)} placeholder="ID de orden..." color="blue" />

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Observaciones</label>
            <textarea rows={2} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
              value={form.observaciones} onChange={e=>set('observaciones',e.target.value)} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Firma del cliente al recibir</label>
            {entrega?.firma_cliente && <div className="mb-2"><p className="text-xs text-gray-500 mb-1">Firma guardada:</p><img src={entrega.firma_cliente} alt="firma" className="border rounded-lg max-h-20 bg-gray-50"/></div>}
            <p className="text-xs text-gray-500 mb-1">{entrega?.firma_cliente?'Nueva firma (reemplaza la anterior):':'Firmar aquí (dedo o stylus):'}</p>
            <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white touch-none">
              <SignatureCanvas ref={sigRef} penColor="#1a1a2e" canvasProps={{className:'w-full',style:{height:150,touchAction:'none'}}} />
            </div>
            <div className="flex gap-2 mt-2">
              <input className="flex-1 border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                placeholder="Aclaración (nombre completo)" value={form.aclaracion_firma_cliente} onChange={e=>set('aclaracion_firma_cliente',e.target.value)} />
              <button onClick={()=>sigRef.current?.clear()} className="text-sm text-white bg-red-500 hover:bg-red-600 active:bg-red-700 px-4 py-2.5 rounded-xl">Borrar</button>
            </div>
          </div>
        </div>

        <div className="flex gap-3 px-4 sm:px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-200 rounded-xl">Cancelar</button>
          <button onClick={guardar} disabled={saving}
            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60 text-white px-5 py-2.5 rounded-xl text-sm font-medium flex-1 sm:flex-none">
            <Save className="w-4 h-4" /> {saving?'Guardando...':esEdicion?'Actualizar':'Registrar entrega'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AsignarModal({ entrega, clientes, tecnicos, onClose, onSave }) {
  const [tecnico_id, setTecnicoId] = useState(entrega?.tecnico_id || '');
  const [nombre_tecnico, setNombreTecnico] = useState(entrega?.nombre_tecnico || '');
  const [cliente_id, setClienteId] = useState(entrega?.cliente_id || '');
  const [nombre_cliente, setNombreCliente] = useState(entrega?.nombre_cliente || '');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    await onSave({ tecnico_id: tecnico_id||null, nombre_tecnico: nombre_tecnico||null,
                   cliente_id: cliente_id||null, nombre_cliente: nombre_cliente||null });
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm flex flex-col">
        <div className="flex items-center justify-between px-4 py-4 border-b relative">
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full sm:hidden" />
          <h2 className="text-base font-semibold text-gray-900 mt-1">
            <UserPlus className="inline w-4 h-4 mr-1 text-orange-500" />
            Asignar — {entrega.tipo_documento} {entrega.numero_documento}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-4 py-4 space-y-3">
          <FSelect label="Técnico" value={tecnico_id} onChange={v=>{const t=tecnicos.find(t=>(t.id||t._id)===v);setTecnicoId(v);setNombreTecnico(t?.nombre||t?.username||'');}} color="orange">
            <option value="">Sin asignar</option>
            {tecnicos.map(t=><option key={t.id||t._id} value={t.id||t._id}>{t.nombre||t.username}</option>)}
          </FSelect>
          <FSelect label="Cliente" value={cliente_id} onChange={v=>{const c=clientes.find(c=>(c.id||c._id)===v);setClienteId(v);setNombreCliente(c?.nombre||'');}} color="orange">
            <option value="">Sin asignar</option>
            {clientes.map(c=><option key={c.id||c._id} value={c.id||c._id}>{c.nombre}</option>)}
          </FSelect>
        </div>
        <div className="flex gap-3 px-4 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-200 rounded-xl">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white px-5 py-2.5 rounded-xl text-sm font-medium flex-1">
            <Save className="w-4 h-4" /> {saving ? 'Guardando...' : 'Guardar asignación'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetalleModal({ entrega: e, onClose, onEdit, onAsignar, esLocked }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b flex-shrink-0 relative">
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-full sm:hidden" />
          <h2 className="text-base font-semibold text-gray-900 capitalize mt-1 sm:mt-0">{e.tipo_documento} {e.numero_documento}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5" /></button>
        </div>
        <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4 space-y-3 text-sm">
          <Row label="Cliente"       value={e.nombre_cliente || e.cliente_id || <span className="text-orange-500">Sin asignar</span>} />
          <Row label="Técnico"       value={e.nombre_tecnico || e.tecnico_id || <span className="text-orange-500">Sin asignar</span>} />
          <Row label="Cargado"       value={e.fecha_carga ? new Date(e.fecha_carga).toLocaleString('es-PY') : '—'} />
          <Row label="Estado"        value={<Badge estado={e.estado}/>} />
          <Row label="Entrega técnico" value={e.fecha_entrega_tecnico?new Date(e.fecha_entrega_tecnico).toLocaleDateString('es-PY'):'—'} />
          <Row label="Entrega cliente" value={e.fecha_entrega_cliente?new Date(e.fecha_entrega_cliente).toLocaleDateString('es-PY'):'—'} />
          {e.observaciones && <Row label="Observaciones" value={e.observaciones} />}
          {e.firma_cliente && (
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Firma cliente</p>
              <img src={e.firma_cliente} alt="firma" className="border rounded-lg max-h-24 bg-gray-50 w-full object-contain" />
              {e.aclaracion_firma_cliente && <p className="text-xs text-gray-600 mt-1">{e.aclaracion_firma_cliente}</p>}
            </div>
          )}
        </div>
        <div className="flex gap-3 px-4 sm:px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-200 rounded-xl">Cerrar</button>
          {!esLocked && (
            <button onClick={onAsignar} className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2.5 text-sm rounded-xl hover:bg-orange-600">
              <UserPlus className="w-4 h-4" /> Asignar
            </button>
          )}
          <button onClick={onEdit} className="flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 text-sm rounded-xl hover:bg-blue-700 flex-1 sm:flex-none">
            <Edit className="w-4 h-4" /> Editar
          </button>
        </div>
      </div>
    </div>
  );
}

function FField({ label, value, onChange, type='text', placeholder, readOnly, color='blue' }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <input type={type} className={`w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-${color}-500 focus:outline-none disabled:bg-gray-50`}
        value={value} onChange={e=>onChange&&onChange(e.target.value)} placeholder={placeholder} readOnly={readOnly} disabled={readOnly} />
    </div>
  );
}

function FSelect({ label, value, onChange, children, color='blue' }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      <select className={`w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-${color}-500 focus:outline-none bg-white`}
        value={value} onChange={e=>onChange(e.target.value)}>{children}</select>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-xs font-medium text-gray-500 w-28 sm:w-36 shrink-0">{label}</span>
      <span className="text-gray-800 flex-1">{value ?? '—'}</span>
    </div>
  );
}
